// components/balance-modal.jsx
import React, { useEffect, useState } from 'react';
import { FaTimes, FaArrowUp, FaCheck, FaCopy } from "react-icons/fa";
import toast, { Toaster } from 'react-hot-toast';
import { apiCall } from '../utils/api';

const BalanceModal = ({ isOpen, onClose, user, onSuccess }) => {
    const [balanceForm, setBalanceForm] = useState({
        serviceType: '',
        amount: '',
        providerId: '',
        transactionId: '',
        remark: '',
        selectedColumns: {
            serviceType: false,
            amount: false,
            providerId: false,
            transactionId: false
        }
    });

    const [loading, setLoading] = useState(false);
    const [providerLoading, setProviderLoading] = useState(false);
    const [providers, setProviders] = useState([]);

    // Fetch providers when modal opens
    useEffect(() => {
        if (isOpen) {
            fetchProviders();
        }
    }, [isOpen]);

    const fetchProviders = async () => {
        setProviderLoading(true);
        try {
            const params = {
                category: "3"
            };

            const result = await apiCall("provider", params, true);

            if (result.status === true) {
                setProviders(result.provider || []);
            } else {
                toast.error(result.message || 'Failed to fetch providers');
            }
        } catch (error) {
            toast.error(error.message || 'Failed to fetch providers');
        } finally {
            setProviderLoading(false);
        }
    };

    // Service Type Options
    const serviceTypes = [
        { id: 'manual_add', label: 'Manual Add', icon: FaArrowUp, color: 'text-green-600' },
        // Additional service types can be uncommented as needed
        /*
        { id: 'manual_debit', label: 'Manual Debit', icon: FaArrowDown, color: 'text-red-600' },
        { id: 'bonus', label: 'Bonus', icon: FaGift, color: 'text-yellow-600' },
        { id: 'payout', label: 'Payout', icon: FaMoneyCheck, color: 'text-blue-600' },
        { id: 'sms_charges', label: 'SMS Charges', icon: FaSms, color: 'text-purple-600' },
        { id: 'server_fees', label: 'Server Fees', icon: FaServer, color: 'text-indigo-600' }
        */
    ];

    // Generate Transaction ID
    function generateTransactionId() {
        const prefix = 'TXN';
        const timestamp = Date.now().toString().slice(-8);
        const random = Math.floor(Math.random() * 10000).toString().padStart(4, '0');
        return `${prefix}${timestamp}${random}`;
    }

    // Convert number to words
    const numberToWords = (num) => {
        if (!num || isNaN(num)) return '';

        const numStr = num.toString();
        const parts = numStr.split('.');
        const integerPart = parseInt(parts[0]);
        const decimalPart = parts[1] ? parseInt(parts[1]) : 0;

        if (integerPart === 0 && decimalPart === 0) return 'Zero';

        let result = '';

        if (integerPart > 0) {
            result += convertIntegerToWords(integerPart);
        }

        if (decimalPart > 0) {
            if (result) result += ' Point ';
            result += convertIntegerToWords(decimalPart);
        }

        return result;
    };

    const convertIntegerToWords = (num) => {
        if (num === 0) return '';

        const ones = ['', 'One', 'Two', 'Three', 'Four', 'Five', 'Six', 'Seven', 'Eight', 'Nine'];
        const tens = ['', '', 'Twenty', 'Thirty', 'Forty', 'Fifty', 'Sixty', 'Seventy', 'Eighty', 'Ninety'];
        const teens = ['Ten', 'Eleven', 'Twelve', 'Thirteen', 'Fourteen', 'Fifteen', 'Sixteen', 'Seventeen', 'Eighteen', 'Nineteen'];

        if (num < 10) {
            return ones[num];
        } else if (num < 20) {
            return teens[num - 10];
        } else if (num < 100) {
            const ten = Math.floor(num / 10);
            const one = num % 10;
            return tens[ten] + (one > 0 ? ' ' + ones[one] : '');
        } else if (num < 1000) {
            const hundred = Math.floor(num / 100);
            const remainder = num % 100;
            return ones[hundred] + ' Hundred' + (remainder > 0 ? ' ' + convertIntegerToWords(remainder) : '');
        } else if (num < 100000) {
            const thousand = Math.floor(num / 1000);
            const remainder = num % 1000;
            return convertIntegerToWords(thousand) + ' Thousand' + (remainder > 0 ? ' ' + convertIntegerToWords(remainder) : '');
        } else if (num < 10000000) {
            const lakh = Math.floor(num / 100000);
            const remainder = num % 100000;
            return convertIntegerToWords(lakh) + ' Lakh' + (remainder > 0 ? ' ' + convertIntegerToWords(remainder) : '');
        }

        return num.toString();
    };

    // Handle form change
    const handleBalanceFormChange = (key, value) => {
        setBalanceForm(prev => ({ ...prev, [key]: value }));
    };

    // Handle column toggle
    const toggleColumn = (column) => {
        setBalanceForm(prev => ({
            ...prev,
            selectedColumns: {
                ...prev.selectedColumns,
                [column]: !prev.selectedColumns[column]
            }
        }));
    };

    // Handle auto-generate transaction ID
    const handleGenerateTransactionId = () => {
        handleBalanceFormChange('transactionId', generateTransactionId());
    };

    // Handle provider selection
    const handleProviderChange = (e) => {
        handleBalanceFormChange('providerId', e.target.value);
    };

    // Submit balance transaction
    const handleSubmitBalance = async () => {
        const allConfirmed = Object.values(balanceForm.selectedColumns).every(value => value === true);

        if (!allConfirmed) {
            toast.error('Please confirm all checkboxes before submitting');
            return;
        }

        if (!balanceForm.serviceType) {
            toast.error('Please select a service type');
            return;
        }
        if (!balanceForm.amount || parseFloat(balanceForm.amount) <= 0) {
            toast.error('Please enter a valid amount');
            return;
        }
        if (!balanceForm.providerId) {
            toast.error('Please choose a provider');
            return;
        }
        if (!balanceForm.transactionId) {
            toast.error('Please enter a valid transaction ID');
            return;
        }

        setLoading(true);
        try {
            const params = {
                userId: user.user_id,
                serviceType: balanceForm.serviceType,
                amount: balanceForm.amount,
                providerId: balanceForm.providerId,
                transactionId: balanceForm.transactionId,
                remark: balanceForm.remark
            };

            console.log(params);

            const result = await apiCall("manage-balance", params, true);

            if (result.status === true) {
                toast.success('Balance updated successfully!');
                if (onSuccess) onSuccess();
                onClose();
            } else {
                toast.error(result.message || 'Failed to update balance');
            }
        } catch (error) {
            toast.error('Network error. Please try again.');
        } finally {
            setLoading(false);
        }
    };

    // Reset form when modal opens
    useEffect(() => {
        if (isOpen) {
            setBalanceForm({
                serviceType: '',
                amount: '',
                providerId: '',
                transactionId: '',
                remark: '',
                selectedColumns: {
                    serviceType: false,
                    amount: false,
                    providerId: false,
                    transactionId: false
                }
            });
        }
    }, [isOpen]);

    if (!isOpen || !user) return null;

    return (
        <div
            className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black bg-opacity-50"
            onClick={(e) => {
                if (e.target === e.currentTarget) {
                    onClose();
                }
            }}
        >
            <div className="bg-white rounded-xl shadow-2xl w-full max-w-3xl max-h-[90vh] flex flex-col">
                {/* Fixed Header - Matching RetailerProfileModal style */}
                <div className="flex-shrink-0 bg-white border-b border-gray-200 px-6 py-4 rounded-t-xl flex justify-between items-center">
                    <div>
                        <h2 className="text-xl font-bold text-gray-900">
                            {user.name} (ID: {user.user_id})
                        </h2>
                        <p className="text-sm text-gray-500">Balance Management</p>
                    </div>
                    <button
                        onClick={onClose}
                        className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
                        disabled={loading}
                    >
                        <FaTimes className="text-gray-400 hover:text-gray-600" />
                    </button>
                </div>

                {/* Scrollable Content - Matching RetailerProfileModal style */}
                <div className="flex-1 overflow-y-auto px-6 py-4">
                    {/* Service Type */}
                    <div className="mb-4">
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                            Choose Service Type <span className="text-red-500">*</span>
                        </label>
                        <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                            {serviceTypes.map((service) => {
                                const Icon = service.icon;
                                const isSelected = balanceForm.serviceType === service.id;
                                return (
                                    <button
                                        key={service.id}
                                        onClick={() => handleBalanceFormChange('serviceType', service.id)}
                                        disabled={loading}
                                        className={`flex items-center gap-2 px-3 py-2 rounded-lg border-2 transition-all text-sm ${
                                            isSelected
                                                ? 'border-blue-500 bg-blue-50 text-blue-700'
                                                : 'border-gray-200 hover:border-gray-300 text-gray-700 hover:bg-gray-50'
                                        } ${loading ? 'opacity-50 cursor-not-allowed' : ''}`}
                                    >
                                        <Icon className={service.color} />
                                        <span className="text-xs sm:text-sm">{service.label}</span>
                                        {isSelected && <FaCheck className="ml-auto text-blue-500 text-xs" />}
                                    </button>
                                );
                            })}
                        </div>
                    </div>

                    {/* Amount */}
                    <div className="mb-4">
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                            Amount <span className="text-red-500">*</span>
                        </label>
                        <div className="relative">
                            <span className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-500">₹</span>
                            <input
                                type="number"
                                step="0.01"
                                value={balanceForm.amount}
                                onChange={(e) => handleBalanceFormChange('amount', e.target.value)}
                                placeholder="Enter amount"
                                disabled={loading}
                                className="w-full pl-8 pr-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent disabled:bg-gray-100"
                            />
                        </div>
                        {balanceForm.amount && parseFloat(balanceForm.amount) > 0 && (
                            <p className="mt-1 text-xs text-red-500">
                                {numberToWords(parseFloat(balanceForm.amount))}
                            </p>
                        )}
                    </div>

                    {/* Provider ID */}
                    <div className="mb-4">
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                            Choose Provider <span className="text-red-500">*</span>
                        </label>
                        <div className="flex flex-wrap gap-2">
                            <select
                                value={balanceForm.providerId}
                                onChange={handleProviderChange}
                                disabled={loading || providerLoading}
                                className="flex-1 min-w-[150px] px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-gray-50 text-sm disabled:bg-gray-100"
                            >
                                <option value="">Select a provider</option>
                                {providers.map((item) => (
                                    <option key={item.id} value={item.id}>
                                        {item.name}
                                    </option>
                                ))}
                            </select>
                            {providerLoading && (
                                <span className="text-sm text-gray-500 flex items-center">
                                    <svg className="animate-spin h-4 w-4 mr-2" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                                    </svg>
                                    Loading...
                                </span>
                            )}
                        </div>
                    </div>

                    {/* Transaction ID */}
                    <div className="mb-4">
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                            Transaction ID <span className="text-red-500">*</span>
                        </label>
                        <div className="flex flex-wrap gap-2">
                            <input
                                type="text"
                                value={balanceForm.transactionId}
                                onChange={(e) => handleBalanceFormChange('transactionId', e.target.value)}
                                disabled={loading}
                                className="flex-1 min-w-[150px] px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-gray-50 text-sm disabled:bg-gray-100"
                            />
                            <button
                                onClick={() => {
                                    navigator.clipboard.writeText(balanceForm.transactionId);
                                    toast.success('Transaction ID copied!');
                                }}
                                disabled={loading}
                                className="px-3 py-2 bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors disabled:opacity-50"
                                title="Copy Transaction ID"
                            >
                                <FaCopy className="text-gray-600" />
                            </button>
                        </div>
                        <p
                            className="mt-1 text-xs text-blue-600 cursor-pointer hover:underline"
                            onClick={handleGenerateTransactionId}
                        >
                            Click to auto-generate new transaction ID
                        </p>
                    </div>

                    {/* Remark */}
                    <div className="mb-4">
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                            Remark
                        </label>
                        <textarea
                            value={balanceForm.remark}
                            onChange={(e) => handleBalanceFormChange('remark', e.target.value)}
                            placeholder="Add a remark (optional)"
                            rows="2"
                            disabled={loading}
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-none disabled:bg-gray-100"
                        />
                    </div>

                    {/* Column Selection */}
                    <div className="border-t border-gray-200 pt-4">
                        <p className="text-sm font-medium text-gray-700 mb-3">Please confirm the following:</p>
                        <div className="flex flex-wrap gap-4">
                            <label className="flex items-center gap-2 text-sm text-gray-600 cursor-pointer">
                                <input
                                    type="checkbox"
                                    checked={balanceForm.selectedColumns.serviceType}
                                    onChange={() => toggleColumn('serviceType')}
                                    disabled={loading}
                                    className="w-4 h-4 text-blue-600 rounded border-gray-300 focus:ring-blue-500 disabled:opacity-50"
                                />
                                <span>Service Type</span>
                            </label>
                            <label className="flex items-center gap-2 text-sm text-gray-600 cursor-pointer">
                                <input
                                    type="checkbox"
                                    checked={balanceForm.selectedColumns.amount}
                                    onChange={() => toggleColumn('amount')}
                                    disabled={loading}
                                    className="w-4 h-4 text-blue-600 rounded border-gray-300 focus:ring-blue-500 disabled:opacity-50"
                                />
                                <span>Amount</span>
                            </label>
                            <label className="flex items-center gap-2 text-sm text-gray-600 cursor-pointer">
                                <input
                                    type="checkbox"
                                    checked={balanceForm.selectedColumns.providerId}
                                    onChange={() => toggleColumn('providerId')}
                                    disabled={loading}
                                    className="w-4 h-4 text-blue-600 rounded border-gray-300 focus:ring-blue-500 disabled:opacity-50"
                                />
                                <span>Provider</span>
                            </label>
                            <label className="flex items-center gap-2 text-sm text-gray-600 cursor-pointer">
                                <input
                                    type="checkbox"
                                    checked={balanceForm.selectedColumns.transactionId}
                                    onChange={() => toggleColumn('transactionId')}
                                    disabled={loading}
                                    className="w-4 h-4 text-blue-600 rounded border-gray-300 focus:ring-blue-500 disabled:opacity-50"
                                />
                                <span>Transaction ID</span>
                            </label>
                        </div>
                        {!Object.values(balanceForm.selectedColumns).every(v => v === true) && (
                            <p className="mt-2 text-xs text-red-500">
                                Please check all confirmations before submitting
                            </p>
                        )}
                    </div>
                </div>

                {/* Fixed Footer - Matching RetailerProfileModal style */}
                <div className="flex-shrink-0 bg-gray-50 border-t border-gray-200 px-6 py-4 rounded-b-xl flex flex-col sm:flex-row gap-3">
                    <button
                        onClick={handleSubmitBalance}
                        disabled={loading}
                        className="flex-1 px-6 py-2.5 bg-blue-600 hover:bg-blue-700 text-white font-medium rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center"
                    >
                        {loading ? (
                            <>
                                <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                                </svg>
                                Processing...
                            </>
                        ) : (
                            'Submit Transaction'
                        )}
                    </button>
                    <button
                        onClick={onClose}
                        disabled={loading}
                        className="flex-1 px-6 py-2.5 bg-gray-200 hover:bg-gray-300 text-gray-700 font-medium rounded-lg transition-colors disabled:opacity-50"
                    >
                        Cancel
                    </button>
                </div>
            </div>
            <Toaster position="top-right" />
        </div>
    );
};

export default BalanceModal;